import { useCallback, useEffect, useState } from 'react'
import { completeTask, describeDue, loadLetters, type LetterTask } from '@core/letters'
import { Card } from '@design/components'

/**
 * What someone has asked of you, on the parents' screen.
 *
 * The member interface is deliberately one screen with one button (§6), and
 * this is the only thing that has earned a place beside it — because it is not
 * something they have to remember to check. It appears when there is something,
 * it is announced by a push notification, and it disappears the moment it is
 * ticked. Rule 23, doing real work: on any ordinary day this renders nothing
 * at all.
 *
 * Row-level security is what makes this safe to put on a shared screen. A
 * member's session can physically only read tasks assigned to them — not the
 * lawyer's letter, not the restaurant's invoices. See 0018.
 */
export function ForYou({ userId, refreshKey }: { userId: string; refreshKey?: number }) {
  const [tasks, setTasks] = useState<LetterTask[]>([])
  const [busy, setBusy] = useState<string | null>(null)

  const refresh = useCallback(() => {
    // Both kinds: a letter handed to a parent is as much "for you" as a chore.
    loadLetters('all')
      .then((all) => setTasks(all.filter((t) => !t.doneAt && t.assignedTo === userId)))
      .catch(() => setTasks([]))
  }, [userId])

  useEffect(refresh, [refresh, refreshKey])

  if (tasks.length === 0) return null

  return (
    <section className="mb-4">
      <h2 className="text-sm text-muted mb-2">Voor jou</h2>
      {tasks.map((task) => (
        <Card key={task.id} className="mb-2 flex items-start gap-3">
          <button
            aria-label="Gedaan"
            disabled={busy === task.id}
            onClick={async () => {
              setBusy(task.id)
              try {
                await completeTask(task, userId)
                refresh()
              } finally {
                setBusy(null)
              }
            }}
            className="shrink-0 mt-0.5 w-9 h-9 rounded-full border-2 border-line active:border-ink"
          />
          <div className="min-w-0">
            <p className="font-medium text-lg">{task.title}</p>
            {task.assignmentNote && <p className="text-muted">{task.assignmentNote}</p>}
            {task.dueDate && (
              <p className="text-sm text-muted mt-0.5">{describeDue(task.dueDate)}</p>
            )}
          </div>
        </Card>
      ))}
    </section>
  )
}
